/**
 * # dxfBridge.conformantSkeleton.tableOwners (T9b — record owners + LAYER pointers)
 *
 * Small, barrel-free helpers the conformant-skeleton back-fill uses to wire
 * every symbol-table RECORD to its table head and every LAYER to its
 * PlotStyleName / MATERIAL objects on the FROM-SCRATCH export path:
 *
 *   - {@link readTableHeadHandle} / {@link readSkeletonTableHeadHandles} — the
 *     `5 <hex>` of each captured table head (`symbolTableHeaders[<TABLE>]`,
 *     `linetypeTableHeader`). R13+ requires every symbol-table record to carry
 *     `330 <table-head handle>` (Autodesk DXF reference "Common Symbol Table
 *     Group Codes"; ezdxf writes it on every LTYPE / LAYER / STYLE / DIMSTYLE /
 *     BLOCK_RECORD). A record without it is silently re-homed by ezdxf on load
 *     (no audit fix record — the audit oracle was blind to it) and rejected /
 *     recovered by AutoCAD.
 *   - {@link findPlotStyleNormalHandle} / {@link findMaterialHandleByName} —
 *     resolve, from the skeleton's verbatim OBJECTS stream, the `Normal`
 *     ACDBPLACEHOLDER of the ACAD_PLOTSTYLENAME dictionary (LAYER `390`, R2000+)
 *     and the `Global` MATERIAL (LAYER `347`, R2007+). These are the ezdxf
 *     `Layer.set_required_attributes` defaults (`plotstyle_handle` →
 *     `plotstyles["Normal"]`, `material_handle` → `materials["Global"]` — the
 *     R2018 reference output carries `347 <Global>` on layer `0`) and what
 *     AutoCAD writes on every layer of an R2007+ drawing (a layer's default
 *     material is `Global`; `ByLayer` / `ByBlock` are ENTITY-level materials).
 *     A pointer is set ONLY when the target object is actually emitted — never
 *     fabricated — and a copied pointer survives ONLY when it targets an
 *     emitted object of the RIGHT type ({@link collectLayerPointerTargets}).
 *   - {@link wireSymbolTableOwners} — the pure projection that applies both to
 *     the FINAL (post-remint) style tables / block records / linetypes.
 *
 * Source of truth: Autodesk DXF reference (R2018) + ezdxf 1.4.3 reference
 * output (`ezdxf.new("R2018")`). AutoCAD MCP is not used.
 */
import { isTopLevelGroup, trackNestedBlockDepth } from "@dxf-ai-editor/dxf-io";

import { isHexHandle } from "./dxfBridge.conformantSkeleton.handles";
import type { BlockRecord, DrawingDocument } from "./model";

/** Structural shape of one raw DXF group as stored on the document. */
type RawGroup = { code: number; value: string };

/**
 * The own group-5 handle of a captured table head (the groups between
 * `2 <TABLE>` and the first `0 <RECORD>`): the FIRST top-level `5`. A head
 * never contains a `0` marker, but a record boundary still terminates the
 * scan defensively. `undefined` when the head carries no handle.
 */
export function readTableHeadHandle(
  groups: readonly RawGroup[] | undefined,
): string | undefined {
  if (!groups) return undefined;
  for (const g of groups) {
    if (g.code === 0) break;
    if (g.code === 5) {
      const handle = g.value.trim();
      return handle.length > 0 ? handle : undefined;
    }
  }
  return undefined;
}

/** The table-head handles a from-scratch document adopts from the skeleton. */
export interface SymbolTableHeadHandles {
  layer?: string;
  style?: string;
  dimstyle?: string;
  blockRecord?: string;
  ltype?: string;
}

/**
 * Read the five table-head handles the skeleton template carries
 * (`symbolTableHeaders.LAYER/STYLE/DIMSTYLE/BLOCK_RECORD` + `linetypeTableHeader`).
 * A missing / handle-less head yields `undefined` for that table.
 */
export function readSkeletonTableHeadHandles(
  skeleton: Pick<DrawingDocument, "symbolTableHeaders" | "linetypeTableHeader">,
): SymbolTableHeadHandles {
  const heads = skeleton.symbolTableHeaders ?? {};
  const out: SymbolTableHeadHandles = {};
  const layer = readTableHeadHandle(heads.LAYER);
  const style = readTableHeadHandle(heads.STYLE);
  const dimstyle = readTableHeadHandle(heads.DIMSTYLE);
  const blockRecord = readTableHeadHandle(heads.BLOCK_RECORD);
  const ltype = readTableHeadHandle(skeleton.linetypeTableHeader);
  if (layer !== undefined) out.layer = layer;
  if (style !== undefined) out.style = style;
  if (dimstyle !== undefined) out.dimstyle = dimstyle;
  if (blockRecord !== undefined) out.blockRecord = blockRecord;
  if (ltype !== undefined) out.ltype = ltype;
  return out;
}

/** One raw OBJECTS-section record: its type, own handle and TOP-LEVEL groups. */
interface RawObjectRecord {
  type: string;
  handle: string | undefined;
  /** Groups outside any `102 {…}` block (reactor / xdictionary payload skipped). */
  topLevel: RawGroup[];
}

/**
 * Split a flat OBJECTS group stream into records (one per `0 <TYPE>` marker).
 * The own handle is the FIRST top-level `5`; groups inside a `102 {…}` block
 * (persistent reactors / extension dictionary) are excluded from `topLevel` so
 * a nested `330` / `5` is never mistaken for a record-level field.
 */
function splitRawObjectRecords(
  groups: readonly RawGroup[] | undefined,
): RawObjectRecord[] {
  const out: RawObjectRecord[] = [];
  if (!groups) return out;
  let current: RawObjectRecord | undefined;
  let depth = 0;
  for (const g of groups) {
    if (g.code === 0) {
      current = { type: g.value.trim(), handle: undefined, topLevel: [] };
      out.push(current);
      depth = 0;
      continue;
    }
    if (!current) continue;
    depth = trackNestedBlockDepth(g, depth);
    if (!isTopLevelGroup(g, depth)) continue;
    current.topLevel.push(g);
    if (g.code === 5 && current.handle === undefined) {
      const handle = g.value.trim();
      if (handle.length > 0) current.handle = handle;
    }
  }
  return out;
}

/**
 * Read the `3 <name> → 350|360 <handle>` child entries of a DICTIONARY-shaped
 * record (top-level groups only) as a name → handle map.
 */
function dictionaryEntries(record: RawObjectRecord): Map<string, string> {
  const entries = new Map<string, string>();
  let pendingName: string | undefined;
  for (const g of record.topLevel) {
    if (g.code === 3) {
      pendingName = g.value.trim();
      continue;
    }
    if ((g.code === 350 || g.code === 360) && pendingName !== undefined) {
      entries.set(pendingName, g.value.trim());
      pendingName = undefined;
    }
  }
  return entries;
}

/**
 * The handle of the ROOT DICTIONARY's `<entryName>` child (e.g. `ACAD_LAYOUT`
 * → the layout dictionary) in a raw OBJECTS stream. The root dictionary is the
 * first `0 DICTIONARY` record (the DXF OBJECTS section opens with it; its own
 * `330 0`). `undefined` when absent — never fabricated.
 */
export function findRootDictionaryEntryHandle(
  rawObjectGroups: readonly RawGroup[] | undefined,
  entryName: string,
): string | undefined {
  const root = splitRawObjectRecords(rawObjectGroups).find(
    (r) => r.type.toUpperCase() === "DICTIONARY",
  );
  if (!root) return undefined;
  const handle = dictionaryEntries(root).get(entryName);
  return handle !== undefined && handle.length > 0 ? handle : undefined;
}

/**
 * The own handle of the `Normal` PlotStyleName ACDBPLACEHOLDER — the LAYER
 * `390` default. Resolution follows the real object graph: the ROOT
 * DICTIONARY's `ACAD_PLOTSTYLENAME` child → that ACDBDICTIONARYWDFLT's `Normal`
 * entry → an emitted `ACDBPLACEHOLDER` with that handle. When the dictionary
 * chain is absent (a trimmed template) the SOLE placeholder in the stream is
 * used; with zero or several unresolvable placeholders nothing is returned
 * (a fabricated / ambiguous pointer is worse than none).
 */
export function findPlotStyleNormalHandle(
  rawObjectGroups: readonly RawGroup[] | undefined,
): string | undefined {
  const records = splitRawObjectRecords(rawObjectGroups);
  const placeholders = new Set<string>();
  for (const r of records) {
    if (r.type.toUpperCase() === "ACDBPLACEHOLDER" && r.handle !== undefined) {
      placeholders.add(r.handle.toLowerCase());
    }
  }
  if (placeholders.size === 0) return undefined;
  // Walk ROOT DICTIONARY → ACAD_PLOTSTYLENAME dict → `Normal` entry.
  const byHandle = new Map<string, RawObjectRecord>();
  for (const r of records) {
    if (r.handle !== undefined) byHandle.set(r.handle.toLowerCase(), r);
  }
  for (const r of records) {
    if (r.type.toUpperCase() !== "DICTIONARY") continue;
    const plotStyleDictHandle = dictionaryEntries(r).get("ACAD_PLOTSTYLENAME");
    if (plotStyleDictHandle === undefined) continue;
    const plotStyleDict = byHandle.get(plotStyleDictHandle.toLowerCase());
    if (!plotStyleDict) continue;
    const normal = dictionaryEntries(plotStyleDict).get("Normal");
    if (normal !== undefined && placeholders.has(normal.toLowerCase())) {
      return normal;
    }
  }
  if (placeholders.size === 1) {
    const [only] = placeholders;
    return only.toUpperCase();
  }
  return undefined;
}

/**
 * The own handle of the MATERIAL object whose `1` name equals `name`
 * (case-insensitive; e.g. `Global` — the LAYER `347` default). `undefined`
 * when the template emits no such material.
 */
export function findMaterialHandleByName(
  rawObjectGroups: readonly RawGroup[] | undefined,
  name: string,
): string | undefined {
  const wanted = name.toLowerCase();
  for (const r of splitRawObjectRecords(rawObjectGroups)) {
    if (r.type.toUpperCase() !== "MATERIAL" || r.handle === undefined) continue;
    const materialName = r.topLevel.find((g) => g.code === 1)?.value.trim();
    if (materialName !== undefined && materialName.toLowerCase() === wanted) {
      return r.handle;
    }
  }
  return undefined;
}

/** The LAYER pointer defaults resolved from the skeleton's OBJECTS stream. */
export interface LayerPointerDefaults {
  plotStyleHandle?: string;
  materialHandle?: string;
}

/**
 * The OWN handles (lowercased) of the objects a LAYER pointer may legally
 * target: `390` → an emitted `ACDBPLACEHOLDER` (a PlotStyleName entry),
 * `347` → an emitted `MATERIAL`. Built by {@link collectLayerPointerTargets}.
 */
export interface LayerPointerTargets {
  plotStyleHandles: ReadonlySet<string>;
  materialHandles: ReadonlySet<string>;
}

/**
 * Collect the typed pointer-target sets from the OBJECTS stream the export
 * emits verbatim. T9b review round 1 (P2) — a copied `390` / `347` was
 * previously kept whenever it matched ANY emitted own handle, so a layer whose
 * `plotStyleHandle` happened to equal a LINE's handle (or whose
 * `materialHandle` equalled the placeholder's) was emitted pointing at an
 * object of the WRONG type — a pointer AutoCAD / ezdxf reject or re-home.
 * PlotStyleName placeholders and MATERIAL objects reach the export ONLY
 * through this raw stream (the typed model carries no MATERIAL /
 * ACDBPLACEHOLDER objects), so these two sets are the complete legal targets.
 */
export function collectLayerPointerTargets(
  rawObjectGroups: readonly RawGroup[] | undefined,
): LayerPointerTargets {
  const plotStyleHandles = new Set<string>();
  const materialHandles = new Set<string>();
  for (const r of splitRawObjectRecords(rawObjectGroups)) {
    if (r.handle === undefined) continue;
    const type = r.type.toUpperCase();
    if (type === "ACDBPLACEHOLDER") plotStyleHandles.add(r.handle.toLowerCase());
    else if (type === "MATERIAL") materialHandles.add(r.handle.toLowerCase());
  }
  return { plotStyleHandles, materialHandles };
}

/** Input of {@link wireSymbolTableOwners}. */
export interface WireSymbolTableOwnersInput {
  styleTables: DrawingDocument["styleTables"];
  blockRecords: Record<string, BlockRecord>;
  linetypes: DrawingDocument["linetypes"] | undefined;
  heads: SymbolTableHeadHandles;
  layerDefaults: LayerPointerDefaults;
  /** The typed legal targets of a LAYER `390` / `347` pointer. */
  pointerTargets: LayerPointerTargets;
}

/** Output of {@link wireSymbolTableOwners} — fresh objects, input untouched. */
export interface WiredSymbolTables {
  styleTables: DrawingDocument["styleTables"];
  blockRecords: Record<string, BlockRecord>;
  linetypes: DrawingDocument["linetypes"] | undefined;
}

/**
 * Return `record` with its `330` owner set to `head`; when the table head
 * carries no handle the owner is REMOVED (never fabricated, and a copied owner
 * pointing at a foreign head must not survive onto the skeleton's tables).
 */
function withOwner<T extends { ownerHandle?: string }>(
  record: T,
  head: string | undefined,
): T {
  const next: T = { ...record };
  if (head === undefined) delete next.ownerHandle;
  else next.ownerHandle = head;
  return next;
}

/**
 * Resolve a LAYER pointer (`390` / `347`): a valid hex `current` that targets an
 * EMITTED object OF THE RIGHT TYPE (`legalTargets` — the placeholders for `390`,
 * the materials for `347`) is kept verbatim; anything else (absent, non-hex,
 * dangling, or pointing at an entity / object of another type) falls back to
 * the skeleton default (which may itself be `undefined` → the field is left
 * unset — never fabricated).
 */
function resolvePointer(
  current: string | undefined,
  fallback: string | undefined,
  legalTargets: ReadonlySet<string>,
): string | undefined {
  if (
    current !== undefined &&
    isHexHandle(current) &&
    legalTargets.has(current.toLowerCase())
  ) {
    return current;
  }
  return fallback;
}

/**
 * Pure projection: wire every LAYER / STYLE / DIMSTYLE / BLOCK_RECORD / LTYPE
 * record's `330` owner to its table head and every LAYER's `390` / `347`
 * pointers (see the module doc). Own handles are never touched — this runs
 * AFTER the record remint and reads only reference fields.
 */
export function wireSymbolTableOwners(
  input: WireSymbolTableOwnersInput,
): WiredSymbolTables {
  const { heads, layerDefaults, pointerTargets } = input;

  let styleTables = input.styleTables;
  if (styleTables) {
    const next = { ...styleTables };
    if (styleTables.layers) {
      next.layers = styleTables.layers.map((layer) => {
        const owned = withOwner(layer, heads.layer);
        const {
          plotStyleHandle: currentPlot,
          materialHandle: currentMaterial,
          ...rest
        } = owned;
        const plotStyleHandle = resolvePointer(
          currentPlot,
          layerDefaults.plotStyleHandle,
          pointerTargets.plotStyleHandles,
        );
        const materialHandle = resolvePointer(
          currentMaterial,
          layerDefaults.materialHandle,
          pointerTargets.materialHandles,
        );
        return {
          ...rest,
          ...(plotStyleHandle !== undefined ? { plotStyleHandle } : {}),
          ...(materialHandle !== undefined ? { materialHandle } : {}),
        };
      });
    }
    if (styleTables.textStyles) {
      next.textStyles = styleTables.textStyles.map((s) =>
        withOwner(s, heads.style),
      );
    }
    if (styleTables.dimensionStyles) {
      next.dimensionStyles = styleTables.dimensionStyles.map((s) =>
        withOwner(s, heads.dimstyle),
      );
    }
    styleTables = next;
  }

  const blockRecords: Record<string, BlockRecord> = {};
  for (const [name, record] of Object.entries(input.blockRecords)) {
    blockRecords[name] = withOwner(record, heads.blockRecord);
  }

  let linetypes: DrawingDocument["linetypes"] | undefined;
  if (input.linetypes) {
    linetypes = {};
    for (const [name, lt] of Object.entries(input.linetypes)) {
      linetypes[name] = withOwner(lt, heads.ltype);
    }
  }

  return { styleTables, blockRecords, linetypes };
}
